// Shopify sync health (N8, shared/sync-health.ts over the synced job_status rows): the sidebar footer's line and the
// plans' warning when the stock pull is behind (the plan works from that stock).
import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { stockStale, syncHealth } from "../../../shared/sync-health.ts";
import { Link } from "../lib/router.tsx";
import { useRows } from "../lib/sync.ts";
import { cx } from "../ui/controls.tsx";
import { Notice } from "../ui/PageParts.tsx";
import { useShopifyWrites } from "./CatalogEditor.tsx";

/** The sidebar footer's line: green "Shopify in sync", or the worst job's state; nothing before Shopify is set up. */
/** Now, again every 5 minutes: health is about age, so it moves on with nothing synced changing. */
function useNow() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 300_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export function SyncHealthLine({ rail = false }: { rail?: boolean }) {
  const rows = useRows("job_status");
  const now = useNow();
  const { problems } = useShopifyWrites();
  const receipts = useRows("receipt").filter((r) => r.shopify_status === "failed").length;
  const h = syncHealth(rows, problems.length + receipts, now);
  if (h.level === "none") return null;
  return (
    <Link
      to="/settings/shopify"
      className={cx("flex min-w-0 items-center gap-2 rounded pb-1.5 text-[11px] text-faint hover:text-text", rail ? "justify-center" : "px-2")}
      title={[h.text, ...h.jobs.map((j) => j.text)].join("\n")}
    >
      <span className={cx("size-1.5 shrink-0 rounded-full", h.level === "ok" ? "bg-ok" : h.level === "warn" ? "bg-warn" : "bg-danger")} />
      {!rail && <span className={cx("truncate", h.level === "bad" && "text-danger", h.level === "warn" && "text-warn")}>{h.text}</span>}
    </Link>
  );
}

/** Both plans: the stock they plan from is old, because the stock pull has been failing or hasn't run. */
export function StockStaleBanner() {
  const rows = useRows("job_status");
  const stale = stockStale(rows, useNow());
  if (!stale) return null;
  return (
    <Notice tone={stale.level === "bad" ? "danger" : "warn"}>
      {stale.text}. The plan works from that stock, so check what's on the shelf before ordering.{" "}
      <Link to="/settings/shopify" className="underline">
        Settings → Shopify
      </Link>
    </Notice>
  );
}
